import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { EditorView } from 'prosemirror-view'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import { InlineEditActions } from '@/inline-edit/store/slice'
import { selectInlineEditSelection, selectInlineEditIsSubmitting, selectInlineEditPendingReplacement } from '@/inline-edit/store/selectors'
import { getAnchors } from '@/inline-edit/util/get-anchors'
import { kHighlightClassSelection, kHighlightClassPending } from '@/inline-edit/constants/highlight'
import { useSelectionTracking } from './useSelectionTracking'
import { usePopoverPosition } from './usePopoverPosition'
import type { UserFilesRAGRequest } from '@/agent/chat_form/store/schemas'

type ResearchFilters = {
  caselawSearchFilter?: unknown
  lrrSearchFilter?: unknown
  userFilesRag?: UserFilesRAGRequest | null
  filePaths?: string[]
}

type UseInlineEditProps = {
  conversationId: string
  artifactVersionId?: string
  isReviewingDiff: boolean
  researchFilters?: ResearchFilters
}

/**
 * Facade hook for the inline edit feature. Composes useSelectionTracking
 * (DOM events) and usePopoverPosition (PM coords) into a clean API.
 *
 * Owns highlight state internally — the parent passes `highlightDecoration`
 * straight through to MarkdownEditor without interpreting it.
 *
 * The parent communicates external events (accept, reject, insert) via
 * lifecycle signals (`onReviewComplete`, `onInsertComplete`).
 */
export function useInlineEdit({ conversationId, artifactVersionId, isReviewingDiff, researchFilters }: UseInlineEditProps) {
  const appDispatch = useAppDispatch()
  // selection is stored in Redux so it survives component remounts (the parent dialog can remount on resize)
  const selection = useAppSelector((state: RootState) => selectInlineEditSelection(state, { conversationId }))
  const isSubmitting = useAppSelector((state: RootState) => selectInlineEditIsSubmitting(state, { conversationId }))
  const pendingReplacement = useAppSelector((state: RootState) => selectInlineEditPendingReplacement(state, { conversationId }))

  // ── Internal state ──

  const getEditorViewRef = useRef<(() => EditorView | null) | null>(null)

  // Active selection range + cursor position (head used for popover anchor)
  const [currentRange, setCurrentRange] = useState<{ from: number; to: number; head: number } | null>(null)
  // Blue highlight on inserted text after "Insert Below"
  const [resultHighlight, setResultHighlight] = useState<{ from: number; to: number } | null>(null)

  // ── Stable callbacks ──

  const getEditorView = useCallback(() => getEditorViewRef.current?.() ?? null, [])

  const handleEditorReady = useCallback((getView: () => EditorView | null) => {
    getEditorViewRef.current = getView
  }, [])

  const handleDismiss = useCallback(() => {
    setResultHighlight(null)
  }, [])

  // ── Sub-hooks ──

  const { committedSelection, containerRef, handleSelectionChange } = useSelectionTracking({
    isEditInProgress: isSubmitting || isReviewingDiff,
    onDismiss: handleDismiss,
  })

  const anchorPos = currentRange?.head ?? null
  const { popoverRect, scrollContainerRef } = usePopoverPosition({
    getEditorView,
    anchorPos,
  })

  // ── React to committed selection changes ──

  const prevCommittedSelection = useRef(committedSelection)

  useEffect(() => {
    const prev = prevCommittedSelection.current
    prevCommittedSelection.current = committedSelection

    if (committedSelection) {
      if (!artifactVersionId) {
        setCurrentRange(null)
        appDispatch(InlineEditActions.clearSelection({ conversationId }))
        return
      }

      const view = getEditorView()
      if (!view) return

      const { from, to, text, head } = committedSelection
      const anchors = getAnchors(view, from, to)
      if (!anchors) {
        setCurrentRange(null)
        appDispatch(InlineEditActions.clearSelection({ conversationId }))
        return
      }

      const { anchorStart, anchorEnd } = anchors
      appDispatch(InlineEditActions.setSelection({ conversationId, selection: { text, anchorStart, anchorEnd, from, to } }))
      setCurrentRange({ from, to, head })
    } else if (prev !== null) {
      // Selection was cleared (click outside or blur) — prev check prevents spurious clear on mount
      setCurrentRange(null)
      appDispatch(InlineEditActions.clearSelection({ conversationId }))
    }
  }, [committedSelection, conversationId, artifactVersionId, appDispatch, getEditorView])

  // ── Selection change gating ──

  const gatedHandleSelectionChange = useCallback(
    (sel: { text: string; from: number; to: number; head: number } | null) => {
      if (isSubmitting || isReviewingDiff) return
      handleSelectionChange(sel)
    },
    [isSubmitting, isReviewingDiff, handleSelectionChange]
  )

  // ── Highlight derivation ──

  const highlightDecoration = useMemo(() => {
    if (resultHighlight) {
      return { ...resultHighlight, className: kHighlightClassSelection }
    }
    if ((isSubmitting || isReviewingDiff) && selection) {
      return { from: selection.from, to: selection.to, className: kHighlightClassPending }
    }
    if (currentRange && popoverRect) {
      return { from: currentRange.from, to: currentRange.to, className: kHighlightClassSelection }
    }
    return null
  }, [resultHighlight, isSubmitting, isReviewingDiff, selection, currentRange, popoverRect])

  // ── Derived values ──

  const selectionRange = useMemo<{ from: number; to: number } | null>(() => {
    if (selection) return { from: selection.from, to: selection.to }
    if (currentRange) return { from: currentRange.from, to: currentRange.to }
    return null
  }, [selection, currentRange])

  // ── Handlers ──

  const submit = useCallback(
    (instruction: string) => {
      if (!artifactVersionId) return
      // selection (with from/to) is already in Redux from the committedSelection effect —
      // it persists through submit/loading/review until explicitly cleared
      setCurrentRange(null)
      window.getSelection()?.removeAllRanges()
      appDispatch(
        InlineEditActions.submitInlineEdit({
          conversationId,
          artifactVersionId,
          editInstruction: instruction,
          caselawSearchFilter: researchFilters?.caselawSearchFilter,
          lrrSearchFilter: researchFilters?.lrrSearchFilter,
          userFilesRag: researchFilters?.userFilesRag,
          filePaths: researchFilters?.filePaths,
        })
      )
    },
    [conversationId, artifactVersionId, appDispatch, researchFilters]
  )

  const dismiss = useCallback(() => {
    setCurrentRange(null)
    appDispatch(InlineEditActions.clearSelection({ conversationId }))
  }, [conversationId, appDispatch])

  const cancelEdit = useCallback(() => {
    appDispatch(InlineEditActions.cancelInlineEdit({ conversationId }))
    appDispatch(InlineEditActions.clearSelection({ conversationId }))
    setResultHighlight(null)
  }, [appDispatch, conversationId])

  const onReviewComplete = useCallback(() => {
    appDispatch(InlineEditActions.clearSelection({ conversationId }))
  }, [appDispatch, conversationId])

  // onInsertComplete must be called AFTER dismiss() — dismiss does NOT touch resultHighlight
  const onInsertComplete = useCallback(
    (range: { from: number; to: number }) => {
      setResultHighlight(range)
      appDispatch(InlineEditActions.clearSelection({ conversationId }))
    },
    [appDispatch, conversationId]
  )

  return {
    // State
    highlightDecoration,
    popoverAnchorRect: popoverRect,
    selection,
    selectionRange,
    isSubmitting,
    hasPendingResult: !!pendingReplacement,

    // Handlers
    submit,
    dismiss,
    cancelEdit,
    onReviewComplete,
    onInsertComplete,

    // DOM integration
    getEditorView,
    editorContainerRef: containerRef,
    scrollContainerRef,
    handleEditorReady,
    handleSelectionChange: gatedHandleSelectionChange,
  }
}
